import { GridColDef } from "@material-ui/data-grid";
import { useHistory } from "react-router-dom";
import { ChipsCell, EntityTable } from "../../components/EntityTable";
import { PageHeader } from "../../components/PageHeader";
import { useSysadminData } from "../../SysadminData";
import { sysadminPaths } from "../../sysadminUtils";

interface OfficeRow {
    id: string;
    officeName: string;
    officeAddress: string;
    companyName: string;
    eaterGroups: number;
    admins: string[];
}

const columns = (showCompany: boolean): GridColDef[] => [
    { field: "officeName", headerName: "Name", flex: 1, minWidth: 180 },
    { field: "companyName", headerName: "Company", flex: 1, minWidth: 160, hide: !showCompany },
    { field: "officeAddress", headerName: "Address", flex: 1, minWidth: 180 },
    { field: "eaterGroups", headerName: "Eater groups", type: "number", width: 150 },
    {
        field: "admins", headerName: "Admins", flex: 1, minWidth: 220, sortable: false,
        renderCell: p => <ChipsCell values={(p.row as OfficeRow).admins} />,
    },
    { field: "id", headerName: "Id", width: 300, hide: true },
];

/** Offices, optionally limited to one company (used on the company page). */
export const OfficesTable: React.FC<{ companyId?: string; title?: string }> = ({ companyId, title }) => {
    const data = useSysadminData();
    const history = useHistory();

    const rows: OfficeRow[] = data.offices
        .filter(({ content }) => (companyId ? content.companyId === companyId : data.matchesSelectedCompany(content.companyId)))
        .map(({ content }) => ({
            id: content.id,
            officeName: content.officeName,
            officeAddress: content.officeAddress,
            companyName: data.companyById(content.companyId)?.companyName ?? "Unknown company",
            eaterGroups: data.eaterGroups.filter(g => g.content.officeId === content.id).length,
            admins: content.officeAdminIds ?? [],
        }));

    const prefillCompanyId = companyId ?? data.selectedCompanyId;
    const selectedName = !companyId && data.selectedCompanyId ? data.companyById(data.selectedCompanyId)?.companyName : undefined;

    return (
        <EntityTable<OfficeRow>
            title={title}
            rows={rows}
            columns={columns(!companyId)}
            getRowId={row => row.id}
            searchText={row => [row.officeName, row.officeAddress, row.companyName, ...row.admins].join(" ")}
            searchPlaceholder="Search name, company, address, admin"
            onRowClick={row => history.push(sysadminPaths.detail("offices", row.id))}
            createLabel="New office"
            onCreate={() => history.push(sysadminPaths.create("offices", prefillCompanyId ? { companyId: prefillCompanyId } : undefined))}
            loading={data.loading}
            error={data.error}
            onRetry={data.reload}
            emptyText={companyId ? "This company has no offices yet" : selectedName ? `${selectedName} has no offices yet` : "No offices yet"}
        />
    );
};

export const Offices: React.FC = () => (
    <div>
        <PageHeader title="Offices" subtitle="Offices (kitchens) belong to a company and serve one or more eater groups." />
        <OfficesTable />
    </div>
);
